import { logger } from '../services/logger.ts';
import React, { useState } from 'react';
import { Shield, X } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';

interface UserRoleModalProps {
  userId: string;
  currentRole: string;
  onClose: () => void;
  onUpdate: () => void;
}

export function UserRoleModal({ userId, currentRole, onClose, onUpdate }: UserRoleModalProps) {
  const [role, setRole] = useState(currentRole);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const isAdmin = user?.app_metadata?.role === 'admin';

  const handleUpdateRole = async () => {
    if (!isAdmin) {
      setError('Only admins can update user roles');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.rpc('admin_update_user_role', {
        target_user_id: userId,
        new_role: role
      });

      if (updateError) throw updateError;

      onUpdate();
      onClose();
    } catch (err) {
      logger.error('Error updating role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Shield className="w-5 h-5 text-wayja-teal" />
            Update User Role
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {!isAdmin && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded">
            You need admin privileges to update user roles
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            disabled={loading || !isAdmin}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateRole}
            className="px-4 py-2 bg-wayja-teal text-white rounded hover:bg-opacity-90 disabled:opacity-50"
            disabled={loading || !isAdmin || role === currentRole}
          >
            {loading ? 'Updating...' : 'Update Role'}
          </button>
        </div>
      </div>
    </div>
  );
}